import { Box, Card, Text } from "@wix/design-system";
import { useRef, useState, type FC } from "react";
import { useAppContext } from "../Common/useAppContext";
import { MobileBottomSheet } from "../MobileBottomSheet/MobileBottomSheet";
import { MobileBottomSheetHeader } from "../MobileBottomSheet/MobileBottomSheetHeader";
import { MenuItems } from "./MenuItems";
import { ProfileDetails } from "./ProfileDetails";
import { ThemeDropdown } from "./theme/ThemeDropdown";
import { useTranslation } from "@wix/fe-essentials-standalone";
import { HelpDropdown } from "../HelpDropdown/HelpDropdown";
import { useMobile } from "../../../hooks/useMobile";

interface MainMenuProps {
  isOpen: boolean;
  closeDropdown: () => void;
  setIsOpen?: (isOpen: boolean) => void;
}

const Desktop: FC<MainMenuProps> = ({ closeDropdown }) => {
  const { userDetails } = useAppContext();
  const [isThemeDropdownVisible, setIsThemeDropdownVisible] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement>(null);

  if (!userDetails) {
    return null;
  }

  return (
    <Box
      align="right"
      width="260px"
      position="absolute"
      {...(document.documentElement.dir === "rtl"
        ? { left: "16px" }
        : { right: "16px" })}
      top="70px"
      borderRadius="8px"
      zIndex="1000"
    >
      <Card showShadow stretchVertically className="profile-dropdown-card">
        <Card.Content size="none">
          <Box direction="vertical" gap={2} padding={4}>
            <ProfileDetails />
            <MenuItems setIsThemeDropdownVisible={setIsThemeDropdownVisible} />
          </Box>
        </Card.Content>
      </Card>
      <ThemeDropdown
        isVisible={isThemeDropdownVisible}
        onClose={() => {
          setIsThemeDropdownVisible(false);
          closeDropdown();
        }}
        ref={themeDropdownRef}
      />
    </Box>
  );
};

const Mobile: FC<MainMenuProps> = ({ isOpen, setIsOpen, closeDropdown }) => {
  const [isThemeDropdownVisible, setIsThemeDropdownVisible] = useState(false);
  const { t } = useTranslation();
  const [isHelpDropdownVisible, setIsHelpDropdownVisible] = useState(false);
  return (
    <>
      <MobileBottomSheet
        open={isOpen}
        onClose={closeDropdown}
        fitContent={true}
      >
        <MobileBottomSheetHeader content={<ProfileDetails />} />
        <Box direction="vertical" gap={2} padding={4}>
          <MenuItems
            setIsThemeDropdownVisible={setIsThemeDropdownVisible}
            setIsHelpDropdownVisible={setIsHelpDropdownVisible}
          />
        </Box>
      </MobileBottomSheet>
      {/* DARK MODE BOTTOM SHEET */}
      <MobileBottomSheet
        open={isThemeDropdownVisible}
        onClose={() => {
          setIsThemeDropdownVisible(false);
        }}
        fitContent
      >
        <MobileBottomSheetHeader
          content={<Text>{t("landing.header.theme")}</Text>}
          onClose={() => {
            setIsThemeDropdownVisible(false);
          }}
          onBackClick={() => {
            setIsOpen?.(true);
            setIsThemeDropdownVisible(false);
          }}
        />
        <ThemeDropdown
          isVisible={isThemeDropdownVisible}
          onClose={() => {
            setIsThemeDropdownVisible(false);
          }}
        />
      </MobileBottomSheet>
      <MobileBottomSheet
        open={isHelpDropdownVisible}
        onClose={() => {
          setIsHelpDropdownVisible(false);
        }}
        fitContent
      >
        <MobileBottomSheetHeader
          content={<Text>{t("landing.header.help")}</Text>}
          onClose={() => {
            setIsHelpDropdownVisible(false);
          }}
          onBackClick={() => {
            setIsOpen?.(true);
            setIsHelpDropdownVisible(false);
          }}
        />
        <HelpDropdown
          isVisible={isHelpDropdownVisible}
          onClose={() => {
            setIsHelpDropdownVisible(false);
          }}
        />
      </MobileBottomSheet>
    </>
  );
};

const _MainMenu = ({
  isOpen,
  closeDropdown,
  setIsOpen,
}: {
  isOpen: boolean;
  closeDropdown: () => void;
  setIsOpen?: (isOpen: boolean) => void;
}) => {
  const isMobile = useMobile();

  if (isMobile) {
    return (
      <MainMenu.Mobile
        isOpen={isOpen}
        closeDropdown={closeDropdown}
        setIsOpen={setIsOpen}
      />
    );
  }

  if (isOpen) {
    return <MainMenu.Desktop isOpen={isOpen} closeDropdown={closeDropdown} />;
  }

  return null;
};

export const MainMenu = Object.assign(_MainMenu, {
  Mobile,
  Desktop,
});
